import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ConnectAccountOnboarding } from '@stripe/react-connect-js';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CreditCard,
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  Loader2,
  ArrowRight,
  RefreshCw,
  Clock,
  XCircle,
  Zap,
  Copy,
  Check,
  Landmark,
} from 'lucide-react';
import { clinicPaymentService } from '@/services/clinicPaymentService';
import { ClinicPaymentConfig as PaymentConfig } from '@/lib/types';
import { ClinicFinancialDashboard } from '@/components/stripe/ClinicFinancialDashboard';
import { StripeConnectProvider } from '@/components/stripe/StripeConnectProvider';
import { useClinic } from '@/contexts/ClinicContext';
import { useToast } from '@/hooks/use-toast';

// ─── Copy button ──────────────────────────────────────────────────────────────

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
    >
      {copied
        ? <Check className="h-3.5 w-3.5 text-emerald-500" />
        : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}
    </div>
    <Skeleton className="h-12 rounded-xl" />
    <Skeleton className="h-28 rounded-2xl" />
  </div>
);

// ─── Embedded onboarding form ─────────────────────────────────────────────────

const EmbeddedOnboardingForm: React.FC<{
  accountId: string;
  onComplete: () => void;
}> = ({ accountId, onComplete }) => {
  const { toast } = useToast();

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
    >
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/70">
        <div className="w-9 h-9 rounded-xl bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
          <Landmark className="h-4 w-4 text-brand-teal" />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">Complete Stripe Onboarding</p>
          <p className="text-xs text-slate-500 mt-0.5">Enter your business and banking details below.</p>
        </div>
      </div>
      <div className="p-5 min-h-[320px]">
        <StripeConnectProvider accountId={accountId}>
          <ConnectAccountOnboarding
            onExit={() => {
              toast({ title: 'Onboarding updated', description: 'Your Stripe account details have been saved.' });
              onComplete();
            }}
            onStepChange={() => {}}
            onLoadError={({ error }) => {
              toast({
                title: 'Error loading onboarding',
                description: error?.message ?? 'Failed to load Stripe form. Please try again.',
                variant: 'destructive',
              });
            }}
          />
        </StripeConnectProvider>
      </div>
    </motion.div>
  );
};

// ─── Account status view (clinic already has a Stripe account) ────────────────

const StripeAccountStatusView: React.FC<{
  config: PaymentConfig;
  clinicId?: string;
  onMigrated?: () => void;
}> = ({ config, clinicId, onMigrated }) => {
  const pa = config.payment_account;
  const chargesEnabled = pa?.stripe_charges_enabled;
  const payoutsEnabled = pa?.stripe_payouts_enabled;
  const detailsSubmitted = pa?.stripe_details_submitted;
  const accountStatus = pa?.stripe_account_status || 'not_started';

  const onboardingIncomplete = !detailsSubmitted || !chargesEnabled;

  type StatusKey = 'active' | 'onboarding' | 'pending_verification' | 'restricted' | 'rejected' | 'not_started';
  const statusMeta: Record<StatusKey, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
    active:               { label: 'Active',               color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: <CheckCircle2 className="h-5 w-5 text-emerald-500" /> },
    onboarding:           { label: 'Onboarding',           color: 'text-blue-700',    bg: 'bg-blue-50 border-blue-200',       icon: <Clock className="h-5 w-5 text-blue-500" /> },
    pending_verification: { label: 'Pending Verification', color: 'text-blue-700',    bg: 'bg-blue-50 border-blue-200',       icon: <Clock className="h-5 w-5 text-blue-500" /> },
    restricted:           { label: 'Restricted',           color: 'text-amber-700',   bg: 'bg-amber-50 border-amber-200',     icon: <AlertCircle className="h-5 w-5 text-amber-500" /> },
    rejected:             { label: 'Rejected',             color: 'text-red-700',     bg: 'bg-red-50 border-red-200',         icon: <XCircle className="h-5 w-5 text-red-500" /> },
    not_started:          { label: 'Not Started',          color: 'text-slate-600',   bg: 'bg-slate-50 border-slate-200',     icon: <CreditCard className="h-5 w-5 text-slate-400" /> },
  };
  const sm = statusMeta[accountStatus as StatusKey] ?? statusMeta.not_started;

  return (
    <div className="space-y-4">
      {/* ── Three status cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Account status */}
        <div className={`rounded-2xl border p-4 ${sm.bg}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Account</span>
            {sm.icon}
          </div>
          <p className={`text-sm font-bold ${sm.color}`}>{sm.label}</p>
        </div>

        {/* Charges */}
        <div className={`rounded-2xl border p-4 ${chargesEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Charges</span>
            <Zap className={`h-5 w-5 ${chargesEnabled ? 'text-emerald-500' : 'text-slate-400'}`} />
          </div>
          <p className={`text-sm font-bold ${chargesEnabled ? 'text-emerald-700' : 'text-slate-500'}`}>
            {chargesEnabled ? 'Enabled' : 'Not Enabled'}
          </p>
        </div>

        {/* Payouts */}
        <div className={`rounded-2xl border p-4 ${payoutsEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Payouts</span>
            <ShieldCheck className={`h-5 w-5 ${payoutsEnabled ? 'text-emerald-500' : 'text-slate-400'}`} />
          </div>
          <p className={`text-sm font-bold ${payoutsEnabled ? 'text-emerald-700' : 'text-slate-500'}`}>
            {payoutsEnabled ? 'Enabled' : 'Not Enabled'}
          </p>
        </div>
      </div>

      {/* ── Account ID row ── */}
      {pa?.stripe_account_id && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
          <CreditCard className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 flex-shrink-0">
            Account ID
          </span>
          <p className="font-mono text-xs text-slate-600 truncate flex-1 min-w-0">
            {pa.stripe_account_id}
          </p>
          <CopyButton text={pa.stripe_account_id} />
        </div>
      )}

      {/* ── Embedded onboarding (incomplete) ── */}
      {onboardingIncomplete && accountStatus !== 'rejected' && pa?.stripe_account_id && (
        <EmbeddedOnboardingForm
          accountId={pa.stripe_account_id}
          onComplete={() => onMigrated?.()}
        />
      )}

      {/* ── Active ── */}
      {!onboardingIncomplete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-3 px-4 py-4 bg-emerald-50 border border-emerald-200 rounded-2xl"
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-800">Stripe account is active</p>
            <p className="text-xs text-emerald-600 mt-0.5">
              Charges and payouts are enabled. Your clinic is ready to accept payments.
            </p>
          </div>
          <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full">
            Live
          </span>
        </motion.div>
      )}

      {/* ── Rejected ── */}
      {accountStatus === 'rejected' && (
        <div className="flex items-start gap-3 px-4 py-4 bg-red-50 border border-red-200 rounded-2xl">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <XCircle className="h-5 w-5 text-red-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-red-800">Stripe account rejected</p>
            <p className="text-xs text-red-600 mt-0.5">
              Please contact support to review your account status and next steps.
            </p>
          </div>
        </div>
      )}

      {/* ── Financial dashboard ── */}
      {pa?.stripe_account_id && (
        <ClinicFinancialDashboard accountId={pa.stripe_account_id} />
      )}
    </div>
  );
};

// ─── Onboarding prompt (no Stripe account yet) ───────────────────────────────

const StripeOnboardingPrompt: React.FC<{ clinicId: string; onMigrated: () => void }> = ({
  clinicId,
  onMigrated,
}) => {
  const { toast } = useToast();
  const [isStarting, setIsStarting] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);

  const handleStart = async () => {
    setIsStarting(true);
    try {
      const result = await clinicPaymentService.startStripeOnboarding(clinicId);
      if (result?.accountId) {
        setAccountId(result.accountId);
        toast({ title: 'Stripe account created', description: 'Complete the onboarding form below.' });
      }
    } catch (err: any) {
      toast({
        title: 'Could not start onboarding',
        description: err?.message || 'Please try again or contact support.',
        variant: 'destructive',
      });
    } finally {
      setIsStarting(false);
    }
  };

  // Once account is created, show embedded onboarding
  if (accountId) {
    return (
      <EmbeddedOnboardingForm
        accountId={accountId}
        onComplete={onMigrated}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 220 }}
    >
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-start gap-4 px-5 py-5">
          <div className="w-11 h-11 rounded-xl bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
            <CreditCard className="h-5 w-5 text-brand-teal" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-slate-900">Set Up Stripe Payments</p>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              Your clinic needs to complete Stripe onboarding to accept payments and receive payouts.
              This takes about 5 minutes.
            </p>
          </div>
          <Button
            onClick={handleStart}
            disabled={isStarting}
            className="flex-shrink-0 gap-1.5 bg-brand-teal hover:bg-brand-teal/90 text-white text-xs h-8"
          >
            {isStarting
              ? <><Loader2 className="h-3 w-3 animate-spin" />Setting up...</>
              : <>Start Onboarding <ArrowRight className="h-3 w-3" /></>}
          </Button>
        </div>

        {/* Step hints */}
        <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50/70 flex flex-col gap-2">
          {[
            'A Stripe Express account will be created for your clinic',
            'Complete KYC verification in the embedded onboarding form',
            'Once approved, payments and payouts will be enabled automatically',
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span className="w-4 h-4 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span className="text-xs text-slate-500">{step}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

type ViewState = 'loading' | 'no_account' | 'has_account' | 'error';

const ClinicPaymentConfig = () => {
  const { clinic } = useClinic();
  const { toast } = useToast();
  const [viewState, setViewState] = useState<ViewState>('loading');
  const [config, setConfig] = useState<PaymentConfig | null>(null);

  const loadPaymentConfig = useCallback(async () => {
    if (!clinic?.id) return;
    setViewState('loading');
    try {
      const response = await clinicPaymentService.getPaymentAccountStatus(clinic.id);
      if (response?.data) {
        const pa = response.data?.payment_account;
        // Payment account exists but Stripe not created yet → show onboarding prompt
        if (pa && !pa?.stripe_account_id) {
          setViewState('no_account');
          return;
        }
        setConfig(response.data as any);
        setViewState('has_account');
      } else {
        setViewState('no_account');
      }
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const message = err?.response?.data?.error?.message ?? err?.message ?? '';
      if (status === 404 || message.includes('Stripe account not yet created') || message.includes('Start onboarding first')) {
        setViewState('no_account');
      } else {
        setViewState('error');
        toast({ title: 'Failed to load payment config', description: 'Please refresh and try again.', variant: 'destructive' });
      }
    }
  }, [clinic?.id, toast]);

  useEffect(() => { loadPaymentConfig(); }, [loadPaymentConfig]);

  if (viewState === 'loading') return <LoadingSkeleton />;

  if (viewState === 'error') {
    return (
      <div className="flex items-center gap-3 px-4 py-3.5 bg-red-50 border border-red-200 rounded-xl">
        <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="h-4 w-4 text-red-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-800">Could not load payment configuration</p>
          <p className="text-xs text-red-600 mt-0.5">Please refresh and try again.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadPaymentConfig}
          className="flex-shrink-0 h-7 text-xs border-red-200 text-red-600 hover:bg-red-100"
        >
          <RefreshCw className="h-3 w-3 mr-1.5" />
          Retry
        </Button>
      </div>
    );
  }

  if (viewState === 'no_account') {
    return <StripeOnboardingPrompt clinicId={clinic!.id} onMigrated={loadPaymentConfig} />;
  }

  return <StripeAccountStatusView config={config!} clinicId={clinic?.id} onMigrated={loadPaymentConfig} />;
};

export default ClinicPaymentConfig;
